import React, { useState } from 'react';

import { sortBy } from 'lodash';
import { useQueryClient } from 'react-query';
import { removeHtmlTagsFromString } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useProjectTreeStore } from 'store/projectContract.store';

import { ICreateEditCommentPayload, useMutationCreateEditComment } from 'api/mutations/createEditComment.mutation';
import { QueryParams, useQueryGetCommonResources } from 'api/queries/getCommonResources.query';

import classes from 'components/common/Tables/Cells/DetailsCell/components/AddCommentWidget/AddCommentWidget.module.scss';
import RichTextArea from 'components/common/richTextArea/richTextArea';

import { userMentionMapper } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/Narrative/EditNarrative/editNarrative';
import { Button } from 'components/DesignSystem/Button';

interface IAddCommentWidget {
    id: number;
    setIsAddComment: React.Dispatch<React.SetStateAction<boolean>>;
    invalidateQueriesKey: string[];
    isVirtual: boolean;
}

const commonResourcesKey: QueryParams['objectsList'] = ['users'];

export const AddCommentWidget = ({ id, setIsAddComment, invalidateQueriesKey, isVirtual }: IAddCommentWidget) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { project } = useProjectTreeStore();
    const { data } = useQueryGetCommonResources({ objectsList: commonResourcesKey, projectId: project?.id });

    const usersMention = sortBy(data?.users?.map(userMentionMapper), 'text') || [];

    const [singleCommentData, setSingleCommentData] = useState<ICreateEditCommentPayload>({
        cardId: id,
        comment: '',
        id: null,
        mentionedList: [],
        isVirtual: isVirtual,
    });

    const { mutate: createEditCommentMutate } = useMutationCreateEditComment();

    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();

    const handleEditorDescription = (value, mentionedList) => {
        setSingleCommentData({ ...singleCommentData, comment: value, mentionedList });
    };

    const cancelAddCommentMode = () => {
        setSingleCommentData({ ...singleCommentData, comment: '', mentionedList: [] });
        setIsAddComment(false);
    };

    const createComment = () => {
        if (removeHtmlTagsFromString(singleCommentData.comment).length === 0) return;

        const tempSingleCommentData = { ...singleCommentData };
        setIsLoading(true);

        createEditCommentMutate(tempSingleCommentData, {
            onSuccess: () => {
                return Promise.all(invalidateQueriesKey.map((key) => queryClient.invalidateQueries(key))).then(() => {
                    handleSnackBar('Comment Successfully Saved', 'success');
                    setIsAddComment(false);
                    setIsLoading(false);
                });
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
                setIsLoading(false);
            },
        });
    };

    return (
        <div className={classes.addCommentWidgetContainer}>
            <div className={classes.actionButtonsWrapper}>
                <div className={classes.actionButtons}>
                    <Button variant={'secondary'} onClick={cancelAddCommentMode}>
                        Cancel
                    </Button>
                    <Button variant={'primary'} onClick={createComment} isLoading={isLoading}>
                        Add
                    </Button>
                </div>
            </div>

            <RichTextArea
                onEditorDescription={handleEditorDescription}
                existingData={''}
                editorContentHeight={150}
                isAutoFocus={true}
                mention={{
                    separator: ' ',
                    trigger: '@',
                    suggestions: usersMention || [],
                }}
                placeholder={'Enter your comments here. \nType @ to mention another user and notify them about it.'}
            />
        </div>
    );
};
